import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Scale, Subtract, Total } from "@pdfking/math"
import type { PageShape } from "@pdfking/renderer-api"
import { PlotFunction } from "@pdfking/renderer-api"

import { PALETTE } from "#constants"

// A paper plate in `box`, `draw` filling it inside a thin text-colored frame
// set in from its edge: lifts what is printed on it off whatever lies under
// it. The one frame of the book: the posters, the ex libris and the imprint.
// The spot inks it too: the border round the inside and the frame line, over
// the print's. `short` is the shortest side of the page it sits on, which the
// frame is measured by
export const DrawPlate = ({
  box,
  draw,
  short,
}: {
  box: SpatialRectangle<Distance>
  draw: (inside: SpatialRectangle<Distance>) => PageShape[]
  short: Distance
}): PageShape[] => {
  const inset = Scale(short, 0.02)
  const inside = {
    height: Subtract(box.height, Scale(inset, 2)),
    width: Subtract(box.width, Scale(inset, 2)),
    x: Total(box.x, inset),
    y: Total(box.y, inset),
  }
  // The frame line, astride the inside's edges: four bars
  const line = Scale(short, 0.004)
  const half = Scale(line, 0.5)
  const bars = [
    {
      height: line,
      width: Total(inside.width, line),
      x: Subtract(inside.x, half),
      y: Subtract(inside.y, half),
    },
    {
      height: line,
      width: Total(inside.width, line),
      x: Subtract(inside.x, half),
      y: Subtract(Total(inside.y, inside.height), half),
    },
    { height: inside.height, width: line, x: Subtract(inside.x, half), y: inside.y },
    {
      height: inside.height,
      width: line,
      x: Subtract(Total(inside.x, inside.width), half),
      y: inside.y,
    },
  ]
  return [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.paper, position: box },
    // The spot's border: the plate in it, its inside back to paper
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.spot, position: box },
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.paper, position: inside },
    ...draw(inside),
    ...[PALETTE.text, PALETTE.spot].flatMap(fill_color =>
      bars.map(position => ({ enum: PlotFunction.PLOT_RECT, fill_color, position }) as PageShape)
    ),
  ]
}
